import {
  type MessagePathNode,
  type TopicNode,
  MessagePathPartType,
} from "@/shared/domain/topics";
import type {
  Joint,
  JointValue,
} from "@/shared/state/visualization/schema/v1/three-d";
import type { TopicData } from "@/shared/state/visualization/schema/v1/topic-data";

/**
 * Why a declared default binding could not be turned into a real one.
 *
 * These are kept apart rather than collapsed into "not found" because they mean
 * different things to the person reading the row, and each points at a different
 * fix:
 *
 * | Reason | What it tells the user |
 * |---|---|
 * | `topic_absent` | The topic is not in this workspace at all — open the file that has it. |
 * | `path_absent` | The topic is here but the field is not: their recording is a different schema version. |
 * | `name_path_absent` | The sibling array of names the binding indexes by is not published. |
 * | `key_absent` | This joint is not among the names published — a controller publishing a subset. |
 * | `ambiguous` | Several files carry this topic. Nothing is bound, because guessing binds one robot's arm to another's data. |
 * | `not_an_array` | The binding selects an element of a field that is not an array. |
 * | `unnarrowed_array` | The binding names an array with no element selector — see {@link resolveDefaultBindings}. |
 * | `unreadable` | The data could not be read: no representation, no messages, or the fetch failed. |
 */
export type BindingFailureReason =
  | "topic_absent"
  | "path_absent"
  | "name_path_absent"
  | "key_absent"
  | "ambiguous"
  | "not_an_array"
  | "unnarrowed_array"
  | "unreadable";

export interface BindingFailure {
  reason: BindingFailureReason;
  /** A sentence for the joint's row. Names the declaration, never an internal id. */
  message: string;
  /** How many candidates matched. Only meaningful for `ambiguous`. */
  candidates?: number;
}

/** A declaration turned into something the panel can actually sample. */
export interface ResolvedBinding {
  topicData: TopicData;
  /** The declared scale, or 1 — meaning "already in the extension's SI units". */
  scale: number;
}

export interface ResolvedDefaultBindings {
  /** Keyed by **joint name**, the same key `jointValues` and `mimic` use. */
  bound: Record<string, ResolvedBinding>;
  /** Keyed by joint name, for the joints that declared a default and did not get one. */
  unresolved: Record<string, BindingFailure>;
}

/**
 * Reads the values at one message path in a topic's **first** message.
 *
 * Injected rather than imported so this module stays pure and testable: the real
 * one drives `@/shared/reader`'s `ReaderRegistry` (see `firstMessageReader.ts`),
 * which is exactly what the data-server worker does, only from the main thread.
 *
 * Returns `undefined` when the topic has no messages at all.
 */
export type ReadFirstMessage = (
  node: MessagePathNode,
  signal?: AbortSignal,
) => Promise<readonly unknown[] | undefined>;

/**
 * Whether a joint is one this may bind at all: exactly one degree of freedom,
 * and a value of its own rather than one derived from another joint.
 *
 * The consumer-side twin of `roboto.urdf.bindings.is_bindable`, and the rule the
 * wizard's "bind all joints" follows too — a `mimic` joint bound independently
 * is a parallel gripper with two separately driven fingers. Deliberately *not*
 * the same question `jointControlState` answers: that one decides whether to
 * offer a user a control (and so also rejects a joint pinned by its limits or
 * missing an axis), while this one decides whether a *declaration* may exist.
 */
export function isIndependentlyDrivable(joint: Joint): boolean {
  if (joint.mimic !== undefined) {
    return false;
  }
  return (
    joint.type === "revolute" ||
    joint.type === "continuous" ||
    joint.type === "prismatic"
  );
}

/**
 * Resolves each joint's declared default binding against the topics on hand.
 *
 * Keyed by **joint name** throughout — never by entity id — so the caller
 * decides where the result lands. Today that is `mesh.jointValues[name]`.
 *
 * ## Matching is exact
 *
 * Exact topic name, exact message path. No prefix, no glob, no fuzzy match: a
 * glob broad enough to match `/robot1/joint_states` and `/robot2/joint_states`
 * is broad enough to bind one robot's arm to the other robot's data, silently.
 * `findNodesByParts` already defines exactness for this codebase and this
 * follows it; the difference is only that a declaration spells its path as the
 * source schema does (dot-joined `path_in_schema`) rather than as parsed parts,
 * because those carry application internals a vendor extension must not.
 *
 * Several candidates is **not** a tie to break. Two open files can each carry
 * `/joint_states`, and picking one would be picking a robot.
 *
 * ## The array trap
 *
 * Binding an un-narrowed array path does not fail — it silently binds element 0
 * to every joint, because `DataServer.readAllMessages` takes
 * `valuesAtMessagePath(...)[0]`. A robot whose every joint follows joint 0 looks
 * perfectly bound. So a declaration naming an array field with no element
 * selector is refused (`unnarrowed_array`) rather than resolved.
 *
 * ## `elementNamedBy` is a data read, not a string match
 *
 * `sensor_msgs/JointState` correlates `position[i]` with `name[i]` *at runtime,
 * by the publisher*: a URDF cannot know `i`, but it knows the joint's name.
 * Resolving it therefore decodes the topic's **first message**, reads the `name`
 * array, and narrows `position` to `[i, i+1]`. One read per (topic, name path)
 * however many joints share it.
 *
 * **Known limitation:** resolving the index once assumes the publisher's `name`
 * ordering is constant for the whole recording. That holds for every real
 * publisher, but the message definition does not guarantee it — re-resolving per
 * message would need a change to the data server's extraction contract.
 *
 * @param joints the model's joints, as `readJointsFromArticulation` produced them
 * @param topicTrees every topic the workspace has open, across all its files
 * @param readFirstMessage how to decode a first message; see {@link ReadFirstMessage}
 * @param signal aborts the reads; rejects with the signal's reason
 */
export async function resolveDefaultBindings(
  joints: readonly Joint[],
  topicTrees: readonly TopicNode[],
  readFirstMessage: ReadFirstMessage,
  signal?: AbortSignal,
): Promise<ResolvedDefaultBindings> {
  const bound: Record<string, ResolvedBinding> = {};
  const unresolved: Record<string, BindingFailure> = {};

  // One read per (topic, name path), not per joint: a 30-joint arm declares 30
  // bindings into the same `/joint_states.name` array.
  const nameArrays = new Map<string, Promise<readonly unknown[] | undefined>>();

  for (const joint of joints) {
    const binding = joint.defaultBinding;
    if (binding === undefined) {
      continue;
    }
    // A coupled or multi-DOF joint's declaration is ignored rather than
    // reported: the file should not have carried one (our JSON Schema forbids
    // it), and the joint's row already says why it is not driven by data.
    if (!isIndependentlyDrivable(joint)) {
      continue;
    }

    signal?.throwIfAborted();

    const topics = topicTrees.filter((topic) => topic.name === binding.topic);
    if (topics.length === 0) {
      unresolved[joint.name] = {
        reason: "topic_absent",
        message: `Declares ${describe(binding.topic, binding.messagePath)} — that topic is not in this workspace`,
      };
      continue;
    }

    const matches = nodesAtPath(topics, binding.messagePath);
    if (matches.length === 0) {
      unresolved[joint.name] = {
        reason: "path_absent",
        message: `${binding.topic} is here, but it publishes no ${binding.messagePath}`,
      };
      continue;
    }
    if (matches.length > 1) {
      unresolved[joint.name] = {
        reason: "ambiguous",
        message: `${matches.length} files publish ${describe(binding.topic, binding.messagePath)} — pick one to bind this joint`,
        candidates: matches.length,
      };
      continue;
    }

    const node = matches[0];
    const slices = slicePartIndices(node);

    if (binding.element === undefined && binding.elementNamedBy === undefined) {
      if (slices.length > 0) {
        unresolved[joint.name] = {
          reason: "unnarrowed_array",
          message: `${describe(binding.topic, binding.messagePath)} is an array, and the model does not say which element drives this joint`,
        };
        continue;
      }
      record(bound, unresolved, joint.name, node, binding.scale);
      continue;
    }

    // From here an element is being selected, which requires exactly one array
    // to select it from. Narrowing only the last of several would leave an
    // earlier one un-narrowed, and that is the trap above all over again.
    if (slices.length !== 1) {
      unresolved[joint.name] = {
        reason: "not_an_array",
        message:
          slices.length === 0
            ? `${describe(binding.topic, binding.messagePath)} is not an array, so it has no element to select`
            : `${describe(binding.topic, binding.messagePath)} is nested inside more than one array`,
      };
      continue;
    }
    const sliceIndex = slices[0];

    if (binding.element !== undefined) {
      record(
        bound,
        unresolved,
        joint.name,
        narrow(node, sliceIndex, binding.element),
        binding.scale,
      );
      continue;
    }

    // Unreachable: one of the two selectors is set, and `element` was handled
    // above. Written out because narrowing the type is cheaper than asserting it.
    const namedBy = binding.elementNamedBy;
    if (namedBy === undefined) {
      continue;
    }

    const nameNodes = nodesAtPath(topics, namedBy.path);
    if (nameNodes.length !== 1) {
      unresolved[joint.name] = {
        reason: "name_path_absent",
        message:
          nameNodes.length === 0
            ? `${binding.topic} publishes no ${namedBy.path} to look this joint up in`
            : `${nameNodes.length} files publish ${describe(binding.topic, namedBy.path)}`,
        ...(nameNodes.length > 1 ? { candidates: nameNodes.length } : {}),
      };
      continue;
    }
    const nameNode = nameNodes[0];

    const cacheKey = `${nameNode.topic.id} ${namedBy.path}`;
    let names = nameArrays.get(cacheKey);
    if (names === undefined) {
      names = readFirstMessage(nameNode, signal);
      nameArrays.set(cacheKey, names);
    }

    let published: readonly unknown[] | undefined;
    try {
      published = await names;
    } catch (error: unknown) {
      signal?.throwIfAborted();
      unresolved[joint.name] = {
        reason: "unreadable",
        message: `Could not read ${describe(binding.topic, namedBy.path)}: ${messageOf(error)}`,
      };
      continue;
    }

    if (published === undefined) {
      unresolved[joint.name] = {
        reason: "unreadable",
        message: `${binding.topic} carries no messages, so ${namedBy.path} could not be read`,
      };
      continue;
    }

    const index = published.findIndex((value) => String(value) === namedBy.key);
    if (index === -1) {
      unresolved[joint.name] = {
        reason: "key_absent",
        message: `${namedBy.key} is not published on ${binding.topic}`,
      };
      continue;
    }

    record(
      bound,
      unresolved,
      joint.name,
      narrow(node, sliceIndex, index),
      binding.scale,
    );
  }

  return { bound, unresolved };
}

/**
 * The joint values an import should write: what the user already decided, plus
 * the model's own defaults for the joints they have not.
 *
 * > **Bind a joint if and only if `jointValues[name]` is absent.**
 *
 * Absent means "never decided", so the declared default applies. Present means
 * "decided" — by a topic *or* by a constant — and nothing overrides it.
 *
 * Which is exactly why **unbinding a joint must write `{value: 0, scale: 1}`
 * rather than delete its key.** Deleting it restores "never decided", and the
 * next import (or the next open, once anything re-resolves) silently resurrects
 * the binding the user just removed. The rule is one line; the bug it prevents
 * is invisible.
 *
 * Keys naming a joint the new model does not have are dropped, because the
 * result replaces the mesh's `jointValues` wholesale — the same reason the
 * reducer replaces `joints` rather than merging them.
 *
 * @returns the record to store, or `undefined` when nothing needs storing
 */
export function withDefaultBindings(
  joints: readonly Joint[],
  existing: Readonly<Record<string, JointValue>> | undefined,
  bound: Readonly<Record<string, ResolvedBinding>>,
): Record<string, JointValue> | undefined {
  const values: Record<string, JointValue> = {};
  for (const joint of joints) {
    const decided = existing?.[joint.name];
    if (decided !== undefined) {
      values[joint.name] = decided;
      continue;
    }
    const resolved = bound[joint.name];
    if (resolved !== undefined) {
      values[joint.name] = { value: resolved.topicData, scale: resolved.scale };
    }
  }
  return Object.keys(values).length > 0 ? values : undefined;
}

/**
 * Every node under `topics` whose source-schema path is exactly `dotPath`.
 *
 * `path_in_schema` rather than `source_path` because it is already split at the
 * right boundaries: a field whose own name contains a dot stays one level, and
 * re-joining it here can only produce the same string a producer wrote.
 */
function nodesAtPath(
  topics: readonly TopicNode[],
  dotPath: string,
): MessagePathNode[] {
  const found: MessagePathNode[] = [];
  for (const topic of topics) {
    for (const node of topic) {
      if (node.data.path_in_schema.join(".") === dotPath) {
        found.push(node);
      }
    }
  }
  return found;
}

/** Indices of the slice parts in a node's path — one per array it crosses. */
function slicePartIndices(node: MessagePathNode): number[] {
  const indices: number[] = [];
  node.toParts().forEach((part, index) => {
    if (part.type === MessagePathPartType.Slice) {
      indices.push(index);
    }
  });
  return indices;
}

/**
 * The node narrowed to a single element: `position` becomes `position[i:i+1]`.
 *
 * A copy — the tree's own node keeps its full range, so narrowing one joint's
 * view of an array does not narrow every other joint's.
 */
function narrow(
  node: MessagePathNode,
  sliceIndex: number,
  element: number,
): MessagePathNode {
  return node.withModifiedSlicePart(sliceIndex, {
    start: element,
    end: element + 1,
  });
}

/**
 * Records a resolved node, or the reason it could not be sampled.
 *
 * `toTopicData` throws when the field has no representation to read from, which
 * is a real state — a topic whose data was never ingested — not a programming
 * error, so it becomes a row message like any other failure.
 */
function record(
  bound: Record<string, ResolvedBinding>,
  unresolved: Record<string, BindingFailure>,
  jointName: string,
  node: MessagePathNode,
  scale: number | undefined,
): void {
  try {
    bound[jointName] = { topicData: node.toTopicData(), scale: scale ?? 1 };
  } catch (error: unknown) {
    unresolved[jointName] = {
      reason: "unreadable",
      message: `${node.toString()} cannot be read: ${messageOf(error)}`,
    };
  }
}

/** How a declaration is spelled back to the user: topic and path, never joined ambiguously. */
function describe(topic: string, messagePath: string): string {
  return `${topic}.${messagePath}`;
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
